/**
 * The "focus the editor once this tab is active" retry loop — #123.
 *
 * WHY IT EXISTS. Monaco is created a tick or more after `QueryEditorTab`
 * mounts, so the synchronous attempt made when a tab becomes active often
 * finds no editor to focus. The retry covers that gap.
 *
 * WHY IT NEEDS A GUARD, measured rather than reasoned about. Each tick used to
 * call `editor.focus()` unconditionally, and Monaco's `focusTextArea()` ends in
 * `TextAreaWrapper.setSelectionRange`, which calls `textArea.focus()` whenever
 * the textarea is not already the active element. So a tick that lands after
 * the user has moved on does not merely re-assert focus — it TAKES it back.
 *
 * That is what closed the Query Editor results-grid Copy menu on the first
 * click. Full chain, from a real-MSSQL probe (stacks captured in-page):
 *
 *   click → Radix opens the popover and auto-focuses "Copy CSV"
 *         → a retry tick fires ~1-2 ms later
 *         → editor.focus() → focusTextArea → _setHasFocus
 *           → writeScreenReaderContent → setSelectionRange → textArea.focus()
 *         → Radix's DismissableLayer sees `focusin` outside the layer
 *           → onFocusOutside → onDismiss → the popover closes ~2 ms after it
 *             opened, leaving `aria-expanded="false"` and no popper wrapper,
 *             i.e. exactly the "the click did nothing" the user reports.
 *
 * The correlation is what makes this the cause and not a coincidence: across
 * one 10-run batch the tick fired in 2 runs and BOTH lost the popover; it did
 * not fire in the other 8 and NONE of them did.
 *
 * THE GUARD. A tick may take focus only while focus is still where it sat when
 * the tab was activated — that is the state the retry was started to fix. Once
 * some element the user reached AFTER activation holds focus, the retry has no
 * claim on it and stops. `document.body` is not such an element: it is where
 * focus falls when nothing holds it, so it leaves the retry free to run.
 *
 * The pre-existing `tab-edit-input-` check in the caller's `tryFocus` is the
 * same bug found once and patched for one control (renaming a tab). It stays,
 * because it also covers the SYNCHRONOUS first attempt, which this guard
 * deliberately does not: that attempt IS the activation, so the element that
 * activated the tab (a tab button, a toolbar button) must not block it.
 *
 * THE SECOND GUARD — the `body` window. Treating `document.body` as "nothing
 * holds focus" leaves a hole the focus-holder guard above cannot see: while a
 * Radix layer is OPENING, focus passes through `body` for a frame. A tick that
 * lands in that window reads `holder === null`, judges itself free, and takes
 * focus — dismissing the layer exactly as #123 did, just via a different route.
 *
 * Measured on the Object Explorer database context menu (2+ editor tabs open,
 * so a second Monaco is still mounting and the retry is still ticking):
 *
 *   right-click → Radix opens the menu and focuses its content
 *               → content blurs, focus falls to `body`      (+30 ms)
 *               → a retry tick fires, sees `body`, focuses Monaco
 *                 → focusTextArea → textArea.focus()        (+56 ms)
 *               → DismissableLayer sees `focusin` outside → onDismiss
 *                 → the menu is gone ~26 ms after it opened.
 *
 * Same correlation evidence as above: with the retry exhausted (right-click
 * delayed past its ~1 s life) the menu stayed open and the focusin landed on
 * the menu itself; with the retry live it never survived.
 *
 * So a tick must also stand down while a dismissable layer is open. It SKIPS
 * rather than stops: a tab can legitimately be activated from a menu that is
 * still closing, and stopping for good there would leave the editor unfocused.
 * The attempt cap still bounds the whole thing.
 */

/** Cadence of the retry. Exported so the tests cannot drift from the code. */
export const FOCUS_RETRY_INTERVAL_MS = 100;

/** Attempts before giving up — ~1s of Monaco mount time. */
export const FOCUS_RETRY_MAX_ATTEMPTS = 10;

/**
 * Every layer a stolen focus can destroy. The popper wrapper is load-bearing
 * and cannot be replaced by a list of roles: the loose-FK picker's "Add Filter"
 * popover exposes no matching role, and dropping the wrapper let its buttons go
 * unprotected (measured — the spec went straight back to its 30 s stall).
 *
 * Tooltips are filtered out in `defaultIsDismissableLayerOpen` rather than here:
 * they ride the SAME wrapper, so no selector can separate them, but a tooltip
 * holds no focus and cannot be dismissed by taking it — while it does sit under
 * the pointer after a click. Matching one would let a tooltip lingering over
 * "New Query" block the retry for its whole life and leave the editor
 * unfocused: the user clicks, types, and nothing lands.
 */
export const DISMISSABLE_LAYER_SELECTOR =
  '[data-radix-popper-content-wrapper],[role="menu"],[role="listbox"],[role="dialog"][data-state="open"],[data-radix-menu-content]';

/** A layer that only shows a tooltip has nothing for the retry to destroy. */
const isTooltipOnly = (layer: Element): boolean =>
  layer.getAttribute('role') === 'tooltip' || layer.querySelector('[role="tooltip"]') !== null;

export type EditorFocusRetryDeps = {
  /**
   * Focuses the editor. Returns false while Monaco has not mounted yet, which
   * is the only reason to keep retrying. Anything it returns true for is
   * "settled, stop" — including the caller's tab-rename carve-out.
   */
  focusEditor: () => boolean;
  /** Only `activeElement` and `body` are read; injected so this is testable. */
  doc: Pick<Document, 'activeElement' | 'body'>;
  /**
   * Whether a Radix dismissable layer is open right now. Injected for tests;
   * defaults to querying the live document.
   */
  isDismissableLayerOpen?: () => boolean;
};

const defaultIsDismissableLayerOpen = (): boolean => {
  if (typeof document === 'undefined') return false;
  const layers = document.querySelectorAll(DISMISSABLE_LAYER_SELECTOR);
  for (const layer of Array.from(layers)) {
    if (!isTooltipOnly(layer)) return true;
  }
  return false;
};

/** The element actually holding focus, or null when nothing does. */
const focusHolder = (doc: Pick<Document, 'activeElement' | 'body'>): Element | null => {
  const active = doc.activeElement;
  return active && active !== doc.body ? active : null;
};

/**
 * Start retrying until the editor takes focus, focus is claimed elsewhere, or
 * the attempts run out. Returns the cleanup to call when the effect tears down.
 */
export function startEditorFocusRetry(deps: EditorFocusRetryDeps): () => void {
  const focusedAtActivation = focusHolder(deps.doc);
  const isLayerOpen = deps.isDismissableLayerOpen ?? defaultIsDismissableLayerOpen;
  let attempts = 0;

  const intervalId = setInterval(() => {
    attempts += 1;
    const holder = focusHolder(deps.doc);
    // Read BEFORE focusEditor(): once focus has moved, the tick must not fire
    // at all, rather than fire and be judged afterwards.
    const claimedElsewhere = holder !== null && holder !== focusedAtActivation;
    if (claimedElsewhere) {
      clearInterval(intervalId);
      return;
    }
    // An open layer is focus the retry cannot see through: during its opening
    // frame `holder` is `body`, so the check above judges the tick free when it
    // is not. Skip — but keep the loop alive, since the layer may be on its way
    // out and the editor would otherwise never get focused at all.
    if (isLayerOpen()) {
      if (attempts >= FOCUS_RETRY_MAX_ATTEMPTS) clearInterval(intervalId);
      return;
    }
    if (deps.focusEditor() || attempts >= FOCUS_RETRY_MAX_ATTEMPTS) {
      clearInterval(intervalId);
    }
  }, FOCUS_RETRY_INTERVAL_MS);

  return () => clearInterval(intervalId);
}
